import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  UserCog, Loader2, Plus, Pencil, KeyRound, Trash2, Wallet, TrendingUp,
  RefreshCw, Sparkles, Star, ClipboardList, Calendar, Users,
  Bot, Bell, Send, PhoneCall, AlertTriangle, FileText, Save,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  adminListStaff, adminCreateStaff, adminUpdateStaff, adminDeleteStaff,
  adminResetStaffPassword, adminGetStaffProfile, adminRateStaffLog,
  adminRecomputeStaffScore,
  adminUpsertStaffBrief, adminGetStaffBrief, adminListStaffReminders,
  adminForceSendReminder, adminGetStaffInactivity,
  apiErrMsg,
} from '../../lib/api';

const ROLES = [
  { value: 'telegram_support', label: 'Telegram Support' },
  { value: 'video_uploader', label: 'Video Uploader' },
  { value: 'market_analyst', label: 'Market Analyst' },
];

function fmtBdt(v) { return `৳${Math.round(v || 0).toLocaleString('en-IN')}`; }
function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}
function scoreColor(score) {
  const s = Number(score || 0);
  if (s >= 90) return 'text-[hsl(140_65%_70%)]';
  if (s >= 75) return 'text-[hsl(199_84%_70%)]';
  if (s >= 60) return 'text-[hsl(43_92%_72%)]';
  return 'text-[hsl(0_72%_70%)]';
}
function adjustmentColor(pct) {
  const p = Number(pct || 0);
  if (p > 0) return 'text-[hsl(140_65%_70%)]';
  if (p < 0) return 'text-[hsl(0_72%_70%)]';
  return 'text-muted-foreground';
}

export default function AdminStaffTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // staff obj

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminListStaff();
      setItems(r.items || []);
    } catch (e) {
      toast.error(apiErrMsg(e, 'Failed to load staff'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = items.filter((s) => s.active);
  const inactive = items.filter((s) => !s.active);

  return (
    <Card className="card-surface" data-testid="admin-staff-tab">
      <CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <UserCog className="h-5 w-5 text-[hsl(43_92%_56%)]" />
          <div className="font-display text-lg">Staff Workflow & AI Tracking</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            · {items.length} total · {active.length} active
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={load}
              className="h-8 border-white/10 bg-white/[0.02] gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
            <Button size="sm" onClick={() => setCreating(true)}
              className="h-8 bg-[hsl(43_92%_56%)] hover:bg-[hsl(43_92%_62%)] text-black font-bold border-0 gap-1.5"
              data-testid="staff-create-btn">
              <Plus className="h-3.5 w-3.5" /> Add staff
            </Button>
          </div>
        </div>

        {/* Inactivity alerts panel */}
        <InactivityPanel />

        {loading ? (
          <div className="p-12 text-center text-muted-foreground text-sm">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center border border-dashed border-white/10 rounded-md">
            <Users className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
            <div className="font-display text-base">No staff yet</div>
            <div className="text-sm text-muted-foreground mt-1 mb-4">
              Add your first staff member — Telegram support, video uploader, or market analyst.
            </div>
            <Button onClick={() => setCreating(true)}
              className="bg-[hsl(43_92%_56%)] hover:bg-[hsl(43_92%_62%)] text-black font-bold border-0 gap-1.5">
              <Plus className="h-4 w-4" /> Add staff
            </Button>
          </div>
        ) : (
          <div className="grid gap-2">
            {[...active, ...inactive].map((s) => (
              <StaffRow key={s.id} staff={s}
                onOpenProfile={() => setOpenId(s.id)}
                onEdit={() => setEditing(s)}
                refresh={load}
              />
            ))}
          </div>
        )}
      </CardContent>

      {creating && (
        <StaffFormDialog
          open={creating}
          onOpenChange={setCreating}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
      {editing && (
        <StaffFormDialog
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          staff={editing}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {openId && (
        <StaffProfileDialog
          open={!!openId}
          onOpenChange={(v) => !v && setOpenId(null)}
          staffId={openId}
          onChanged={load}
        />
      )}
    </Card>
  );
}

function StaffRow({ staff, onOpenProfile, onEdit, refresh }) {
  return (
    <div
      className={
        'rounded-md border p-3 flex items-center gap-3 flex-wrap ' +
        (staff.active ? 'border-white/10 bg-white/[0.02]' : 'border-white/5 bg-white/[0.01] opacity-60')
      }
      data-testid={`staff-row-${staff.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display text-base truncate">{staff.name}</div>
          <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em] border-white/15">
            {staff.role_label}
          </Badge>
          {!staff.active && (
            <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em] border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)]">
              Deactivated
            </Badge>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground truncate">{staff.email}</div>
      </div>
      <div className="text-right">
        <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">Base</div>
        <div className="font-display text-sm text-[hsl(43_92%_72%)] tabular-nums">{fmtBdt(staff.base_salary_bdt)}</div>
      </div>
      <div className="text-right min-w-[100px]">
        <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">Month score</div>
        {staff.current_month_score != null ? (
          <div className={`font-display text-sm tabular-nums ${scoreColor(staff.current_month_score)}`}>
            {staff.current_month_score} / 100
          </div>
        ) : (
          <div className="text-[11px] text-muted-foreground">—</div>
        )}
      </div>
      <div className="text-right min-w-[110px]">
        <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">Final salary</div>
        <div className="font-display text-sm text-[hsl(140_65%_70%)] tabular-nums">
          {fmtBdt(staff.current_month_salary_bdt || staff.base_salary_bdt)}
        </div>
        {staff.current_month_adjustment_pct !== 0 && staff.current_month_score != null && (
          <div className={`text-[10px] tabular-nums ${adjustmentColor(staff.current_month_adjustment_pct)}`}>
            {staff.current_month_adjustment_pct > 0 ? '+' : ''}{staff.current_month_adjustment_pct}%
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <Button size="sm" variant="outline" onClick={onOpenProfile}
          className="h-8 border-white/10 bg-white/[0.02] gap-1.5"
          data-testid={`staff-view-${staff.id}`}>
          <ClipboardList className="h-3.5 w-3.5" /> View
        </Button>
        <Button size="sm" variant="outline" onClick={onEdit}
          className="h-8 border-white/10 bg-white/[0.02] gap-1.5"
          data-testid={`staff-edit-${staff.id}`}>
          <Pencil className="h-3.5 w-3.5" /> Edit
        </Button>
      </div>
    </div>
  );
}

function StaffFormDialog({ open, onOpenChange, staff, onSaved }) {
  const isEdit = !!staff;
  const [name, setName] = useState(staff?.name || '');
  const [email, setEmail] = useState(staff?.email || '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(staff?.role || 'telegram_support');
  const [baseSalary, setBaseSalary] = useState(staff?.base_salary_bdt || 15000);
  const [whatsappNumber, setWhatsappNumber] = useState(staff?.whatsapp_number || '');
  const [active, setActive] = useState(staff?.active !== false);
  const [saving, setSaving] = useState(false);
  const [resetPwOpen, setResetPwOpen] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await adminUpdateStaff(staff.id, {
          name, role, base_salary_bdt: Number(baseSalary), active,
          whatsapp_number: whatsappNumber,
        });
        toast.success('Staff updated');
      } else {
        if (!password || password.length < 6) {
          toast.error('Password must be 6+ chars');
          setSaving(false);
          return;
        }
        await adminCreateStaff({
          name, email: email.trim().toLowerCase(), password,
          role, base_salary_bdt: Number(baseSalary),
          whatsapp_number: whatsappNumber,
        });
        toast.success('Staff created');
      }
      onSaved();
    } catch (e2) {
      toast.error(apiErrMsg(e2, 'Save failed'));
    } finally { setSaving(false); }
  }

  async function deactivate() {
    if (!isEdit) return;
    if (!confirm(`Deactivate ${staff.name}? They will not be able to log in.`)) return;
    try {
      await adminDeleteStaff(staff.id);
      toast.success('Staff deactivated');
      onSaved();
    } catch (e) { toast.error(apiErrMsg(e, 'Deactivate failed')); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[hsl(220_10%_6%)] border-white/10">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEdit ? `Edit ${staff.name}` : 'Add new staff'}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {isEdit
              ? 'Change role, salary, or activation. Reset password below.'
              : 'Set up staff login. They can log in at /staff/login.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required
              className="bg-white/[0.03] border-white/10" data-testid="staff-form-name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              disabled={isEdit} required
              className="bg-white/[0.03] border-white/10 disabled:opacity-60"
              data-testid="staff-form-email" />
          </div>
          {!isEdit && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Initial password</Label>
              <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)}
                required minLength={6} placeholder="min 6 chars"
                className="bg-white/[0.03] border-white/10 font-mono"
                data-testid="staff-form-password" />
              <div className="text-[10px] text-muted-foreground mt-1">
                Share this with the staff member. They can change it later after login.
              </div>
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Role</Label>
            <select value={role} onChange={(e) => setRole(e.target.value)}
              className="h-9 w-full bg-white/[0.03] border border-white/10 rounded-md px-2 text-sm"
              data-testid="staff-form-role">
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Base salary (BDT)</Label>
            <Input type="number" step="100" min="0" value={baseSalary}
              onChange={(e) => setBaseSalary(e.target.value)} required
              className="bg-white/[0.03] border-white/10 font-mono tabular-nums"
              data-testid="staff-form-salary" />
            <div className="text-[10px] text-muted-foreground mt-1">
              Auto-adjust: 90+ = +10% · 75-89 = base · 60-74 = -5% · &lt;60 = -15%
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
              <PhoneCall className="h-3 w-3" /> WhatsApp number (for AI reminders)
            </Label>
            <Input value={whatsappNumber} onChange={(e) => setWhatsappNumber(e.target.value)}
              placeholder="+8801XXXXXXXXX"
              className="bg-white/[0.03] border-white/10 font-mono"
              data-testid="staff-form-whatsapp" />
            <div className="text-[10px] text-muted-foreground mt-1">
              Claude AI supervisor ei number e 10 min por por reminder pathabe (shift hours only).
            </div>
          </div>
          {isEdit && (
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={active}
                onChange={(e) => setActive(e.target.checked)}
                data-testid="staff-form-active" />
              Active (can log in)
            </label>
          )}
          <div className="flex items-center gap-2 pt-2">
            <Button type="submit" disabled={saving}
              className="flex-1 bg-[hsl(43_92%_56%)] hover:bg-[hsl(43_92%_62%)] text-black font-bold border-0"
              data-testid="staff-form-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              {isEdit ? 'Save changes' : 'Create staff'}
            </Button>
            {isEdit && (
              <>
                <Button type="button" variant="outline" onClick={() => setResetPwOpen(true)}
                  className="h-9 border-white/10 bg-white/[0.02] gap-1.5">
                  <KeyRound className="h-3.5 w-3.5" /> Reset password
                </Button>
                <Button type="button" variant="outline" onClick={deactivate}
                  className="h-9 border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)] hover:bg-[rgba(225,29,46,0.06)] gap-1.5">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </div>
        </form>
      </DialogContent>
      {resetPwOpen && staff && (
        <ResetPasswordDialog open={resetPwOpen} onOpenChange={setResetPwOpen} staff={staff} />
      )}
    </Dialog>
  );
}

function ResetPasswordDialog({ open, onOpenChange, staff }) {
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  async function submit(e) {
    e.preventDefault();
    if (pw.length < 6) { toast.error('Min 6 chars'); return; }
    setSaving(true);
    try {
      await adminResetStaffPassword(staff.id, pw);
      toast.success('Password reset. Share the new password with the staff.');
      onOpenChange(false);
    } catch (e2) { toast.error(apiErrMsg(e2, 'Reset failed')); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-[hsl(220_10%_6%)] border-white/10">
        <DialogHeader>
          <DialogTitle className="font-display">Reset {staff.name}&apos;s password</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)}
            placeholder="new password (6+ chars)" minLength={6}
            className="bg-white/[0.03] border-white/10 font-mono" />
          <Button type="submit" disabled={saving}
            className="w-full bg-[hsl(43_92%_56%)] hover:bg-[hsl(43_92%_62%)] text-black font-bold border-0">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            Reset password
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StaffProfileDialog({ open, onOpenChange, staffId, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminGetStaffProfile(staffId);
      setData(r);
    } catch (e) { toast.error(apiErrMsg(e, 'Failed to load profile')); }
    finally { setLoading(false); }
  }, [staffId]);
  useEffect(() => { if (open) load(); else setData(null); }, [open, load]);

  async function recompute() {
    setRecomputing(true);
    try {
      const r = await adminRecomputeStaffScore(staffId);
      toast.success(`Score: ${r.total_score}/100 · Salary: ${fmtBdt(r.final_salary_bdt)}`);
      await load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(apiErrMsg(e, 'Recompute failed')); }
    finally { setRecomputing(false); }
  }

  async function rateLog(logId, rating) {
    try {
      await adminRateStaffLog(logId, rating);
      toast.success(`Rated ${rating}★`);
      await load();
    } catch (e) { toast.error(apiErrMsg(e, 'Rate failed')); }
  }

  const staff = data?.staff;
  const logs = data?.logs || [];
  const scores = data?.scores || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[hsl(220_10%_6%)] border-white/10 max-h-[92vh] overflow-y-auto"
        data-testid="staff-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <UserCog className="h-5 w-5 text-[hsl(43_92%_56%)]" />
            {staff?.name || 'Staff profile'}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Recent logs, AI scores, monthly performance, and salary breakdown.
          </DialogDescription>
        </DialogHeader>
        {loading || !data ? (
          <div className="p-10 text-center text-muted-foreground text-sm">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            <Card className="bg-white/[0.02] border-white/10">
              <CardContent className="p-3 grid gap-2 sm:grid-cols-[1fr,auto] items-center">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-base">{staff.name}</div>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em] border-white/15">
                      {staff.role_label}
                    </Badge>
                  </div>
                  <div className="text-[11px] text-muted-foreground">{staff.email}</div>
                  <div className="text-[11px] text-muted-foreground">
                    Base salary: <span className="text-[hsl(43_92%_72%)] font-medium">{fmtBdt(staff.base_salary_bdt)}</span>
                  </div>
                </div>
                <Button size="sm" onClick={recompute} disabled={recomputing}
                  className="bg-gradient-to-r from-[hsl(43_92%_56%)] to-[hsl(38_85%_50%)] text-black font-bold border-0 gap-1.5"
                  data-testid="staff-recompute">
                  {recomputing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TrendingUp className="h-3.5 w-3.5" />}
                  Recompute monthly score
                </Button>
              </CardContent>
            </Card>

            {/* AI Supervisor: today's brief + reminders + send-now */}
            <AiSupervisorPanel staffId={staffId} staff={staff} onChanged={onChanged} />

            {/* Scores */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <TrendingUp className="h-3 w-3" /> Monthly scores ({scores.length})
              </div>
              {scores.length === 0 ? (
                <div className="text-xs text-muted-foreground p-3 border border-white/10 rounded-md bg-white/[0.02]">
                  No scores computed yet — click &quot;Recompute&quot; above.
                </div>
              ) : (
                <div className="grid gap-2">
                  {scores.map((s) => (
                    <div key={s.month} className="rounded-md border border-white/10 bg-white/[0.02] p-2.5">
                      <div className="grid gap-2 sm:grid-cols-[70px,1fr,auto]">
                        <div>
                          <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">Month</div>
                          <div className="font-display">{s.month}</div>
                        </div>
                        <div className="grid grid-cols-4 gap-1.5">
                          <MiniS label="Qty" value={s.quantity_score} />
                          <MiniS label="Qual" value={s.quality_score} />
                          <MiniS label="Att" value={s.attendance_score} />
                          <MiniS label="Total" value={s.total_score} bold />
                        </div>
                        <div className="text-right min-w-[100px]">
                          <div className={`text-[11px] font-semibold ${adjustmentColor(s.adjustment_pct)}`}>
                            {s.adjustment_pct > 0 ? '+' : ''}{s.adjustment_pct}%
                          </div>
                          <div className="text-[hsl(140_65%_70%)] font-display text-sm inline-flex items-center gap-1">
                            <Wallet className="h-3 w-3" /> {fmtBdt(s.final_salary_bdt)}
                          </div>
                        </div>
                      </div>
                      {s.ai_monthly_summary && (
                        <div className="mt-1.5 rounded bg-[rgba(140,90,255,0.06)] border border-[rgba(140,90,255,0.25)] px-2 py-1.5">
                          <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(280_70%_75%)] font-semibold inline-flex items-center gap-1">
                            <Sparkles className="h-2.5 w-2.5" /> AI review
                          </div>
                          <div className="text-[11.5px] mt-0.5 whitespace-pre-line">{s.ai_monthly_summary}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Logs */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <ClipboardList className="h-3 w-3" /> Recent logs ({logs.length})
              </div>
              {logs.length === 0 ? (
                <div className="text-xs text-muted-foreground p-3 border border-white/10 rounded-md bg-white/[0.02]">
                  No logs submitted yet.
                </div>
              ) : (
                <div className="grid gap-2">
                  {logs.slice(0, 30).map((l) => (
                    <div key={l.id} className="rounded-md border border-white/10 bg-white/[0.02] p-2.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Calendar className="h-3 w-3" /> {l.date}
                        </div>
                        <span className="text-[11px] font-mono">
                          {Object.entries(l.metrics || {}).map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(' · ')}
                        </span>
                        {l.ai_status === 'done' && l.ai_authenticity_score != null && (
                          <Badge variant="outline" className="text-[10px] uppercase tracking-[0.16em] border-white/15 gap-1">
                            <Sparkles className="h-2.5 w-2.5 text-[hsl(280_70%_75%)]" />
                            AI Q{l.ai_quality_score} · A{l.ai_authenticity_score}
                          </Badge>
                        )}
                        <div className="ml-auto inline-flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button key={n} type="button" onClick={() => rateLog(l.id, n)}
                              className="p-0.5" title={`Rate ${n}★`}>
                              <Star className={`h-3.5 w-3.5 ${n <= (l.admin_rating_1_5 || 0)
                                ? 'text-[hsl(43_92%_56%)] fill-[hsl(43_92%_56%)]'
                                : 'text-white/25 hover:text-[hsl(43_92%_56%)]'}`} />
                            </button>
                          ))}
                        </div>
                      </div>
                      {l.note && <div className="text-xs text-[hsl(0_0%_82%)] mt-1">{l.note}</div>}
                      {l.ai_feedback && (
                        <div className="text-[11px] text-[hsl(280_70%_82%)] mt-1 italic">
                          AI: {l.ai_feedback}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MiniS({ label, value, bold }) {
  return (
    <div className="rounded border border-white/10 bg-white/[0.02] px-1.5 py-1 text-center">
      <div className="text-[8px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`tabular-nums ${bold ? 'font-display text-base ' + scoreColor(value) : 'text-xs'}`}>
        {value}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------
//                    INACTIVITY ALERTS PANEL
// -----------------------------------------------------------------

function InactivityPanel() {
  const [items, setItems] = useState([]);
  const [threshold, setThreshold] = useState(25);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminGetStaffInactivity();
      setItems(r.items || []);
      setThreshold(r.threshold_min || 25);
    } catch { /* soft-fail */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // refresh every 60s
    return () => clearInterval(t);
  }, [load]);

  if (loading && items.length === 0) return null;
  if (items.length === 0) {
    return (
      <div className="rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 flex items-center gap-2">
        <Bot className="h-3.5 w-3.5 text-[hsl(140_65%_70%)]" />
        <span className="text-[11px] text-muted-foreground">
          AI supervisor: All staff responsive. No inactivity alerts.
        </span>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-[rgba(225,29,46,0.55)] bg-[rgba(225,29,46,0.05)] p-3 space-y-2" data-testid="inactivity-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <AlertTriangle className="h-4 w-4 text-[hsl(0_72%_70%)]" />
        <div className="font-display text-sm">Inactivity Alert · {items.length} staff</div>
        <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          &gt; {threshold} min without ack
        </span>
        <Button size="sm" variant="outline" onClick={load}
          className="ml-auto h-7 border-white/10 bg-white/[0.02] gap-1">
          <RefreshCw className="h-3 w-3" /> Refresh
        </Button>
      </div>
      <div className="grid gap-1.5">
        {items.map((it) => (
          <div key={it.staff_id} className="rounded border border-white/10 bg-white/[0.02] px-2.5 py-2 flex items-center gap-2 flex-wrap"
            data-testid={`inactivity-row-${it.staff_id}`}>
            <div className="min-w-0">
              <div className="text-xs font-semibold text-white">{it.staff_name}</div>
              <div className="text-[10px] text-muted-foreground">
                {it.role_label} · {it.staff_email}
              </div>
            </div>
            <Badge variant="outline" className="text-[10px] uppercase tracking-[0.16em] border-[rgba(225,29,46,0.55)] text-[hsl(0_72%_70%)] tabular-nums">
              {Math.round(it.minutes_inactive)} min inactive
            </Badge>
            <div className="text-[10px] text-muted-foreground ml-auto max-w-xs truncate italic">
              Last: {it.last_task_summary}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------
//         AI SUPERVISOR PANEL (inside StaffProfileDialog)
// -----------------------------------------------------------------

function AiSupervisorPanel({ staffId, staff, onChanged }) {
  const [briefText, setBriefText] = useState('');
  const [origBrief, setOrigBrief] = useState('');
  const [savingBrief, setSavingBrief] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, rr] = await Promise.all([
        adminGetStaffBrief(staffId),
        adminListStaffReminders(staffId),
      ]);
      const bt = b.brief?.brief_text || '';
      setBriefText(bt);
      setOrigBrief(bt);
      setReminders(rr.items || []);
    } catch { /* soft-fail */ }
    finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  async function saveBrief() {
    if (!briefText.trim() || briefText.trim().length < 5) {
      toast.error('Brief must be at least 5 characters');
      return;
    }
    setSavingBrief(true);
    try {
      await adminUpsertStaffBrief(staffId, briefText.trim());
      toast.success('Brief saved — reminders will use this context');
      setOrigBrief(briefText);
    } catch (e) { toast.error(apiErrMsg(e, 'Save failed')); }
    finally { setSavingBrief(false); }
  }

  async function sendNow() {
    if (!staff?.whatsapp_number) {
      toast.error('Add a WhatsApp number for this staff first');
      return;
    }
    setSending(true);
    try {
      await adminForceSendReminder(staffId);
      toast.success('Reminder sent ✓');
      await load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(apiErrMsg(e, 'Send failed')); }
    finally { setSending(false); }
  }

  const dirty = briefText !== origBrief;
  const todaysReminders = reminders.slice(0, 15);
  const ackCount = todaysReminders.filter((r) => r.ack_at).length;

  return (
    <Card className="bg-gradient-to-br from-[rgba(140,90,255,0.06)] to-[rgba(70,150,220,0.04)] border border-[rgba(140,90,255,0.25)]">
      <CardContent className="p-3 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Bot className="h-4 w-4 text-[hsl(280_70%_75%)]" />
          <div className="font-display text-sm">AI Supervisor</div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Claude · WhatsApp · Every 10 min
          </span>
          <div className="ml-auto flex items-center gap-1">
            {staff?.whatsapp_number ? (
              <Badge variant="outline" className="text-[9px] uppercase tracking-[0.14em] border-[rgba(70,200,120,0.5)] text-[hsl(140_65%_70%)] gap-1">
                <PhoneCall className="h-2.5 w-2.5" /> {staff.whatsapp_number}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[9px] uppercase tracking-[0.14em] border-[rgba(225,29,46,0.5)] text-[hsl(0_72%_70%)]">
                No WhatsApp
              </Badge>
            )}
          </div>
        </div>

        {/* Brief editor */}
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
            <FileText className="h-3 w-3 text-[hsl(199_84%_68%)]" /> Today&apos;s brief for {staff?.name}
          </Label>
          <textarea
            value={briefText}
            onChange={(e) => setBriefText(e.target.value)}
            placeholder="e.g. Ajke 15 signals dibi. GBP/USD er London session focus. 3 ta live deposit target. Live call kore direct deposit korao — 1% commission pabi."
            rows={4}
            className="w-full bg-white/[0.03] border border-white/10 rounded-md p-2 text-xs leading-relaxed resize-y focus:outline-none focus:border-white/25 mt-1"
            data-testid="brief-textarea"
          />
          <div className="flex items-center gap-2 mt-2">
            <Button size="sm" onClick={saveBrief} disabled={!dirty || savingBrief}
              className="h-8 bg-[hsl(140_65%_45%)] hover:bg-[hsl(140_65%_52%)] text-white gap-1"
              data-testid="brief-save">
              {savingBrief ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Save brief
            </Button>
            <Button size="sm" variant="outline" onClick={sendNow} disabled={sending || !staff?.whatsapp_number}
              className="h-8 border-[hsl(280_70%_50%)] bg-[rgba(140,90,255,0.1)] text-[hsl(280_70%_82%)] gap-1"
              data-testid="brief-send-now">
              {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              Send reminder now
            </Button>
            <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">
              {ackCount}/{todaysReminders.length} acked today
            </span>
          </div>
        </div>

        {/* Recent reminders */}
        {loading ? (
          <div className="text-xs text-muted-foreground text-center py-2">Loading reminders…</div>
        ) : todaysReminders.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-2">
            No reminders sent yet.
          </div>
        ) : (
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-1">
              <Bell className="h-3 w-3" /> Recent reminders ({todaysReminders.length})
            </div>
            {todaysReminders.slice(0, 6).map((r) => {
              const acked = !!r.ack_at;
              return (
                <div key={r.id} className={
                  'rounded border px-2 py-1.5 ' +
                  (acked ? 'border-white/8 bg-white/[0.02] opacity-70' : 'border-[rgba(245,185,66,0.35)] bg-[rgba(245,185,66,0.04)]')
                }>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground tabular-nums">
                      {(r.sent_at || '').slice(11, 16)}
                    </span>
                    <span className="text-[10px] text-white truncate">{r.task_summary}</span>
                    {r.provider === 'mocked' && (
                      <Badge variant="outline" className="text-[8px] uppercase tracking-[0.14em] border-[rgba(245,185,66,0.5)] text-[hsl(43_92%_72%)]">
                        MOCKED
                      </Badge>
                    )}
                    {acked ? (
                      <Badge variant="outline" className="ml-auto text-[8px] uppercase tracking-[0.14em] border-[rgba(70,200,120,0.5)] text-[hsl(140_65%_70%)]">
                        {r.ack_action?.toUpperCase() || 'ACKED'}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="ml-auto text-[8px] uppercase tracking-[0.14em] border-[rgba(245,185,66,0.55)] text-[hsl(43_92%_72%)]">
                        Pending
                      </Badge>
                    )}
                  </div>
                  <div className="text-[10.5px] mt-0.5 text-white/85 whitespace-pre-wrap">
                    {r.message_text}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

